import {
  Button,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { InvoiceStatus, invoiceStatusKorean } from '../../modules/invoices';
import { ClearOutlined, SearchOutlined } from '@ant-design/icons';
import { Vendor } from '../../modules/vendors';
import { searchVendorList } from '../../api/constlink';

const { Option } = Select;
const { Text } = Typography;

const StyledDiv = styled.div`
  .ant-picker-input.ant-input {
    width: 100%;
  }
  .ant-col.ant-form-item-label {
    padding: 0;
  }
  .ant-row.ant-row-center {
    height: 60px;
  }
`;

interface SearchInvoiceComponentProps {
  expenseCommon: any;
  searchData: any;
  enterpriseId: string;
  searchInvoices: (data: any) => void;
  initSearchForm: () => void;
}
function SearchInvoiceComponent({
  expenseCommon,
  searchData,
  enterpriseId,
  searchInvoices,
  initSearchForm,
}: SearchInvoiceComponentProps) {
  const [form] = Form.useForm();
  const dateFormat = 'YYYY-MM-DD';

  const [memberListOptions, setMemberListOptions] = useState<any>();
  const [categoryListOptions, setCategoryListOptions] = useState<any>();
  const [projectListOptions, setProjectListOptions] = useState<any>();
  const [gongjongListOptions, setGongjongListOptions] = useState<any[]>();

  const [data, setData] = useState<Vendor[]>([]);
  const [value, setValue] = useState<string>();

  useEffect(() => {
    let children: any = [];
    children.push(
      <Option key={null} value={-1}>
        {'모든 직원'}
      </Option>,
    );
    for (const member of expenseCommon.memberList) {
      children.push(
        <Option key={member.value} value={member.value}>
          {member.name}
        </Option>,
      );
    }
    setMemberListOptions(children);
    children = [];
    children.push(
      <Option key={null} value={''}>
        {'모든 카테고리'}
      </Option>,
    );
    for (const category of expenseCommon.categoryList.sort((a: any, b: any) =>
      a.name.localeCompare(b.name),
    )) {
      children.push(
        <Option key={category.value} value={category.name}>
          {category.name}
        </Option>,
      );
    }
    setCategoryListOptions(children);
    children = [];
    children.push(
      <Option key={null} value={-1}>
        {'모든 프로젝트'}
      </Option>,
    );
    for (const project of expenseCommon.projectList.sort((a: any, b: any) =>
      b.startDate.localeCompare(a.startDate),
    )) {
      children.push(
        <Option key={project.id} value={project.id}>
          <Tooltip
            title={`${project.name}(${project.startDate} ~ ${project.endDate})`}
          >
            <span>{project.name}</span>
          </Tooltip>
        </Option>,
      );
    }
    setProjectListOptions(children);

    children = [];
    children.push(
      <Option key={null} value={''}>
        {'모든 공정'}
      </Option>,
    );
    for (const gongjong of expenseCommon.gongjongList.sort((a: any, b: any) =>
      a.name.localeCompare(b.name),
    )) {
      children.push(
        <Option key={gongjong.id} value={gongjong.name}>
          {gongjong.name}
        </Option>,
      );
    }
    setGongjongListOptions(children);
  }, [expenseCommon]);

  useEffect(() => {
    if (searchData) {
      form.setFieldsValue({ ...searchData });
    }
  }, [searchData, form]);

  const handleSearch = async (newValue: string) => {
    if (newValue) {
      // fetch(newValue, setData);
      const result = await searchVendorList({
        enterpriseId,
        filterData: { name: newValue },
      });

      setData(result);
    } else {
      setData([]);
    }
  };

  const handleChange = (newValue: string) => {
    setValue(newValue);
  };

  return (
    <StyledDiv>
      <Form
        form={form}
        layout="vertical"
        onFinish={searchInvoices}
        initialValues={searchData}
        style={{ border: '2px solid #bcbcbc', marginRight: '10px' }}
      >
        <Row justify="center" style={{ marginTop: '10px', height: '40px' }}>
          <Col>
            <Form.Item>
              <Space size="middle">
                <Button
                  htmlType="button"
                  onClick={initSearchForm}
                  size="small"
                  icon={<ClearOutlined rev={undefined} />}
                >
                  초기화
                </Button>
                <Button
                  htmlType="submit"
                  type="primary"
                  size="small"
                  icon={<SearchOutlined rev={undefined} />}
                >
                  검색
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Space>
            <Text>내가 승인할 것들</Text>
            <Form.Item
              name="isAskedOfMe"
              label="나에게 요청된 청구서"
              valuePropName="checked"
              noStyle
            >
              <Switch
                checkedChildren="예"
                unCheckedChildren="아니오"
                // defaultChecked
                // checked={false}
              />
            </Form.Item>
          </Space>
        </Row>
        <Row justify="center">
          <Col>
            <Form.Item
              name="startDate"
              label="시작일(청구일 기준)"
              rules={[
                {
                  required: true,
                  message: '시작일을 선택해 주세요.',
                },
              ]}
            >
              <DatePicker format={dateFormat} style={{ width: '180px' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Col>
            <Form.Item
              name="endDate"
              label="종료일(청구일 기준)"
              rules={[
                {
                  required: true,
                  message: '종료일을 선택해 주세요.',
                },
              ]}
            >
              <DatePicker format={dateFormat} style={{ width: '180px' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Col>
            <Form.Item name="status" label="상태">
              <Select style={{ width: '180px' }}>
                <Option key={null} value={''}>
                  모든 상태
                </Option>
                <Option
                  key={InvoiceStatus.APPROVING}
                  value={InvoiceStatus.APPROVING}
                >
                  {invoiceStatusKorean[InvoiceStatus.APPROVING]}
                </Option>
                <Option
                  key={InvoiceStatus.APPROVED}
                  value={InvoiceStatus.APPROVED}
                >
                  {invoiceStatusKorean[InvoiceStatus.APPROVED]}
                </Option>
                <Option
                  key={InvoiceStatus.DECLINED}
                  value={InvoiceStatus.DECLINED}
                >
                  {invoiceStatusKorean[InvoiceStatus.DECLINED]}
                </Option>
                <Option
                  key={InvoiceStatus.PAYMENT_COMPLETED}
                  value={InvoiceStatus.PAYMENT_COMPLETED}
                >
                  {invoiceStatusKorean[InvoiceStatus.PAYMENT_COMPLETED]}
                </Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Col>
            <Form.Item name="name" label="거래처명">
              {/* <Input /> */}
              <Select
                showSearch
                value={value}
                placeholder={'거래처명을 입력하세요'}
                // style={props.style}
                style={{ width: '180px' }}
                allowClear={true}
                defaultActiveFirstOption={false}
                showArrow={false}
                filterOption={false}
                onSearch={handleSearch}
                onChange={handleChange}
                notFoundContent={null}
                options={(data || []).map((d: any) => ({
                  value: d.name,
                  label: d.name,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Col>
            <Form.Item name="project" label="프로젝트">
              <Select style={{ width: '180px' }}>{projectListOptions}</Select>
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Col>
            <Form.Item name="gongjong" label="공정">
              <Select style={{ width: '180px' }}>{gongjongListOptions}</Select>
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Col>
            <Form.Item name="category" label="용도">
              <Select style={{ width: '180px' }}>{categoryListOptions}</Select>
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Col>
            <Form.Item name="member" label="직원">
              <Select style={{ width: '180px' }}>{memberListOptions}</Select>
            </Form.Item>
          </Col>
        </Row>

        <Row justify="center">
          <Col>
            <Form.Item name="isCreditTransaction" label="외상거래">
              <Select style={{ width: '180px' }}>
                <Option key={null} value={''}>
                  전부
                </Option>
                <Option value={true}>예</Option>
                <Option value={false}>아니오</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </StyledDiv>
  );
}

export default SearchInvoiceComponent;
